import { useEffect, useRef } from 'react'

import { useCartStore } from 'modules/cart'
import { getQueryClient } from 'shared/api'
import {
  logMemoryUsage,
  optimizeQueryCache,
  startMemoryCleanupInterval
} from 'shared/utils/memoryOptimization'

/**
 * Hook do zarządzania cleanup i optymalizacji pamięci
 */
export const useMemoryCleanup = () => {
  const { cleanup: cleanupCart } = useCartStore()
  const cleanupIntervalRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return

    if (process.env.NODE_ENV === 'production') {
      cleanupIntervalRef.current = startMemoryCleanupInterval(5 * 60 * 1000)
      logMemoryUsage('App Init')
    }

    const handleVisibilityChange = () => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      ) {
        setTimeout(() => {
          optimizeQueryCache(getQueryClient())
        }, 2000)
      }
    }

    const handlePageHide = () => {
      cleanupCart()
      optimizeQueryCache(getQueryClient())
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', handlePageHide)
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }

      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', handlePageHide)
      }

      if (cleanupIntervalRef.current) {
        cleanupIntervalRef.current()
      }

      cleanupCart()
    }
  }, [cleanupCart])

  const manualCleanup = () => {
    cleanupCart()
    optimizeQueryCache(getQueryClient())
    logMemoryUsage('Manual Cleanup')
  }

  return { manualCleanup }
}
